import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import pick from 'lodash/pick';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';

import MobileContext from 'Shared/contexts/MobileContext';
import { jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';
import { getSortedItems } from 'Shared/utils/sorting';
import { ItemDragLayer } from 'Shared/components/DragAndDropComponents';
import AddItemButtons from 'Shared/components/AddItemButtons/AddItemButtons';
import { updateSelectedUuids, clearSelectedUuids } from 'Shared/actions/selectedUuids';
import { useToggle } from 'Shared/utils/hooks';
import { ItemType } from 'Shared/constants/types';
import { getActionItems } from 'Shared/selectors/meetingItems';
import { NOON_TIME } from 'Shared/constants/constants';
import { addItem } from 'Shared/actions/todos';
import { getCurrentMoment, formatDate, dueDateTimeFormat, convertUnixToMoment } from 'Shared/utils/DateTime.utils';
import {
  homePageTaskAssignedDropAction,
  createHomePageItemAction,
  getHomePageV2DataAction,
} from 'Shared/actions/homePage';
import VideoRecorder from 'Shared/components/VideoRecorder/VideoRecorder';
import ActionItemsTable from 'Shared/components/ActionItems/ActionItemsTable/ActionItemsTable';
import Spinner from 'Shared/components/Spinner';

import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import 'Shared/styles/Animation.scss';
import './HomePageActionItems.scss';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

const sortingMap = {
  actionItems: 'default',
  owner: 'default',
  workspace: 'default',
  dueDate: 'ascending',
};

const TaskWhiteList = Object.freeze([
  'id',
  'uuid',
  'parentUuid',
  'workspacePath',
  'parentText',
  'isHighPriority',
  'workspaceId',
  'assignmentAvatar',
  'positionForHomePage',
  'dueBy',
]);

function ActionItems() {
  const dispatch = useDispatch();
  const { featureFlagMobileAddBtn: showMobileAddButton } = useFlags();

  const [selectedSort, setSelectedSort] = useState(sortingMap);

  const { isXtraSmall } = useResponsiveScreenWidth({
    isAtMost: true,
  });

  const isMobile = useContext(MobileContext);
  const { id: currentUserId } = useCurrentUser();

  const workspacesLoading = useSelector(state => state.loaders.workspacesLoading);

  const actionItems = useSelector(state =>
    getSortedItems({
      items: getActionItems(state).filter(
        ({ completed, assignee: { userId } }) => !completed && userId === currentUserId
      ),
      selectedSort,
      withHighPriority: true,
      positionField: 'positionForHomePage',
    }).map(actionItem => pick(actionItem, TaskWhiteList))
  );

  const todayActionItems = actionItems.filter(
    item => formatDate(item.dueBy, 'YYYY-MM-DD') === formatDate(getCurrentMoment(), 'YYYY-MM-DD')
  );

  const pastDueActionItems = actionItems.filter(item =>
    item?.dueBy ? convertUnixToMoment(item.dueBy).isBefore(getCurrentMoment().startOf('day')) : false
  );

  const futureActionItems = actionItems
    .filter(item =>
      item?.dueBy ? convertUnixToMoment(item.dueBy).isAfter(getCurrentMoment().add(24, 'hours').startOf('day')) : true
    )
    .sort((a, b) => {
      if (!b?.dueBy) return -1;
      return a?.dueBy > b?.dueBy ? 1 : -1;
    });

  const [meetingItemsLoading, meetingItemsLoadingToggle] = useToggle(true);

  const [isShowVideoRecorder, setIsShowVideoRecorder] = useState(false);

  const lastTodoUuid = actionItems[actionItems.length - 1]?.uuid || null;

  useEffect(() => {
    dispatch(clearSelectedUuids());
    return () => {
      dispatch(clearSelectedUuids());
    };
  }, []);

  useEffect(() => {
    dispatch(getHomePageV2DataAction({ completed: false, getAll: true })).then(() => {
      meetingItemsLoadingToggle();
    });
  }, []);

  const handleSelectedItemUpdate = useCallback(
    itemUuid => dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true })),
    [dispatch]
  );

  const handleActionItemDrop = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoverOffset }) =>
      dispatch(
        homePageTaskAssignedDropAction({
          draggedItemUuids,
          hoveredItemUuid: hoveredItemUuid || lastTodoUuid,
          hoverOffset,
          itemsType: ItemType.actionItem,
        })
      ),
    [dispatch, lastTodoUuid]
  );

  const handleAddActionItem = useCallback(
    ({ sourceItemUuid, itemUuid }) =>
      dispatch(
        createHomePageItemAction({
          sourceItemUuid,
          itemUuid,
        })
      ),
    [dispatch]
  );

  const createItem = useCallback(
    ({ body, video } = {}) => {
      const duByeToday = dueDateTimeFormat(moment().add(7, 'days'), NOON_TIME);
      const itemUuid = uuidv4();

      dispatch(addItem({ itemUuid, dueBy: duByeToday, body, video, positionForHomePage: 1 }));
      handleSelectedItemUpdate(itemUuid);
    },
    [dispatch, handleSelectedItemUpdate]
  );

  const handleAddItem = useCallback(() => createItem(), [createItem]);

  const handleAddVideoItem = useCallback(
    video => {
      const body = jsonContentStateFromString(video.audioOnly ? 'New Audio Item' : 'New Video Item');
      createItem({ body, video });
    },
    [createItem]
  );

  const handleVideoRecorderToggle = useCallback(() => setIsShowVideoRecorder(state => !state), []);

  const showEmptyState = actionItems.length === 0 && !meetingItemsLoading;
  const showAddBtn = showMobileAddButton ? !isXtraSmall && !showEmptyState : !showEmptyState;

  return (
    <div className="homepage-action-items">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-2">
        <h4 className="mb-3">Your Action Items</h4>
        {showAddBtn && (
          <AddItemButtons
            block={isMobile}
            disabled={workspacesLoading}
            onAddMeetingItem={handleAddItem}
            onVideoRecorderToggle={handleVideoRecorderToggle}
          />
        )}
      </div>
      <ItemDragLayer />
      {meetingItemsLoading && (
        <div>
          <Spinner activeIf={meetingItemsLoading} />
        </div>
      )}
      {showEmptyState ? (
        <EmptyStateContainer
          subTitle="Your action items across PI Perform will aggregate here.  Get started by creating one for yourself."
          inContainer
          button={
            <WpButton onClick={handleAddItem} disabled={workspacesLoading}>
              <WpIcon name="plus-circle" />
              Add Action Item
            </WpButton>
          }
        />
      ) : (
        <>
          {todayActionItems.length > 0 && (
            <div className="homepage-action-items__section">
              <p className="homepage-action-items__section__title">Due Today</p>
              <ActionItemsTable
                actionItems={todayActionItems}
                selectedSort={selectedSort}
                setSelectedSort={setSelectedSort}
                onActionItemDrop={handleActionItemDrop}
                onCreateItem={handleAddActionItem}
                showTableHeaders={false}
              />
            </div>
          )}
          {pastDueActionItems.length > 0 && (
            <div className="homepage-action-items__section">
              <p className="homepage-action-items__section__title">Past Due</p>
              <ActionItemsTable
                actionItems={pastDueActionItems}
                selectedSort={selectedSort}
                setSelectedSort={setSelectedSort}
                onActionItemDrop={handleActionItemDrop}
                onCreateItem={handleAddActionItem}
                showTableHeaders={false}
              />
            </div>
          )}
          {futureActionItems.length > 0 && (
            <div className="homepage-action-items__section">
              <p className="homepage-action-items__section__title">Upcoming</p>
              <ActionItemsTable
                actionItems={futureActionItems}
                selectedSort={selectedSort}
                setSelectedSort={setSelectedSort}
                onActionItemDrop={handleActionItemDrop}
                onCreateItem={handleAddActionItem}
                showTableHeaders={false}
              />
            </div>
          )}
        </>
      )}
      {isShowVideoRecorder && <VideoRecorder onSave={handleAddVideoItem} onHide={handleVideoRecorderToggle} />}
    </div>
  );
}

export default ActionItems;
